import { Injectable } from '@angular/core';
import moment from 'moment';

/**
 * Interface for week day information
 */
export interface WeekDayInfo {
  header: moment.Moment;
  redDay?: string;
  weekIndex: number;
  weekNumber: number;
}

/**
 * Work hours from an interval that fall within a specific month.
 */
export interface MonthWorkHours {
  year: number;
  month: number;
  monthName: string;
  workHours: number;
}

export interface BusinessYearMonthWorkHoursMap {
  jul: number;
  aug: number;
  sep: number;
  okt: number;
  nov: number;
  dec: number;
  jan: number;
  feb: number;
  mar: number;
  apr: number;
  maj: number;
  jun: number;
}

export interface BusinessYearMonthValueMap {
  jul: number;
  aug: number;
  sep: number;
  okt: number;
  nov: number;
  dec: number;
  jan: number;
  feb: number;
  mar: number;
  apr: number;
  maj: number;
  jun: number;
}

export interface BillableBudgetDistributions {
  vacation: BusinessYearMonthValueMap;
  sick: BusinessYearMonthValueMap;
  internal: BusinessYearMonthValueMap;
}

export interface BillableBudgetAnnualHours {
  vacation: number;
  sick: number;
  internal: number;
}

export interface BillableBudgetInputs {
  semester: number;
  kitscon: number;
  kompetensutveckling: number;
  månadsmöten: number;
  genomsnittSjuk: number;
}

export interface BillableBudgetBreakdown {
  vacation: BusinessYearMonthValueMap;
  sick: BusinessYearMonthValueMap;
  internal: BusinessYearMonthValueMap;
  billable: BusinessYearMonthValueMap;
  yearlyTotal: number;
}

export interface BillableBudgetCalculation {
  own: BillableBudgetBreakdown;
  contractor: BillableBudgetBreakdown;
}

/**
 * Interface for holiday definition
 */
interface HolidayDefinition {
  day: number;
  month: number;
  name: string;
}

/**
 * Interface for Easter date
 */
interface EasterDate {
  year: number;
  day: number;
  month: number;
}

/**
 * Interface for Easter-related holiday offset
 */
interface EasterRelatedHoliday {
  days: number;
  name: string;
}

/**
 * Type for holiday lookup map
 */
type HolidayMap = Record<number, Record<number, Record<number, string>>>;

/**
 * Service for handling Swedish holidays and red days (public holidays).
 * Converted from AngularJS timeTravelMoment factory.
 */
@Injectable({
  providedIn: 'root',
})
export class DateHolidayService {
  private readonly WEEKEND_DAYS = [6, 7]; // Saturday and Sunday (ISO weekday)
  private readonly BUSINESS_YEAR_MONTH_KEYS: Array<keyof BusinessYearMonthValueMap> = [
    'jul',
    'aug',
    'sep',
    'okt',
    'nov',
    'dec',
    'jan',
    'feb',
    'mar',
    'apr',
    'maj',
    'jun',
  ];
  private readonly DEFAULT_BILLABLE_BUDGET_DISTRIBUTIONS: BillableBudgetDistributions = {
    vacation: {
      jul: 44.569313,
      aug: 20.021595,
      sep: 1.216368,
      okt: 2.115423,
      nov: 0.802098,
      dec: 11.202926,
      jan: 7.31143,
      feb: 2.798528,
      mar: 2.298319,
      apr: 1.745224,
      maj: 1.018047,
      jun: 4.900729,
    },
    sick: {
      jul: 3.994233,
      aug: 7.849629,
      sep: 8.666631,
      okt: 8.954985,
      nov: 12.217654,
      dec: 8.757409,
      jan: 10.119079,
      feb: 9.980242,
      mar: 7.283601,
      apr: 6.41854,
      maj: 8.784109,
      jun: 6.973888,
    },
    internal: {
      jul: 0.648766,
      aug: 1.145996,
      sep: 6.76706,
      okt: 12.530189,
      nov: 20.836293,
      dec: 7.269025,
      jan: 9.939859,
      feb: 6.075674,
      mar: 4.721315,
      apr: 6.22721,
      maj: 16.569588,
      jun: 7.269025,
    },
  };

  /**
   * Fixed Swedish public holidays (same date every year)
   */
  private readonly FIXED_HOLIDAYS: HolidayDefinition[] = [
    { day: 1, month: 1, name: 'Nyårsdagen' },
    { day: 6, month: 1, name: 'Trettondag jul' },
    { day: 1, month: 5, name: 'Första maj' },
    { day: 6, month: 6, name: 'Sveriges nationaldag' },
    { day: 24, month: 12, name: 'Julafton' },
    { day: 25, month: 12, name: 'Juldagen' },
    { day: 26, month: 12, name: 'Annandag jul' },
    { day: 31, month: 12, name: 'Nyårsafton' },
  ];

  /**
   * Easter Sunday dates for years 2013-2040
   * Used to calculate movable holidays
   */
  private readonly EASTER_DATES: EasterDate[] = [
    { year: 2013, day: 31, month: 3 },
    { year: 2014, day: 20, month: 4 },
    { year: 2015, day: 5, month: 4 },
    { year: 2016, day: 27, month: 3 },
    { year: 2017, day: 16, month: 4 },
    { year: 2018, day: 1, month: 4 },
    { year: 2019, day: 21, month: 4 },
    { year: 2020, day: 12, month: 4 },
    { year: 2021, day: 4, month: 4 },
    { year: 2022, day: 17, month: 4 },
    { year: 2023, day: 9, month: 4 },
    { year: 2024, day: 31, month: 3 },
    { year: 2025, day: 20, month: 4 },
    { year: 2026, day: 5, month: 4 },
    { year: 2027, day: 28, month: 3 },
    { year: 2028, day: 16, month: 4 },
    { year: 2029, day: 1, month: 4 },
    { year: 2030, day: 21, month: 4 },
    { year: 2031, day: 13, month: 4 },
    { year: 2032, day: 28, month: 3 },
    { year: 2033, day: 17, month: 4 },
    { year: 2034, day: 9, month: 4 },
    { year: 2035, day: 25, month: 3 },
    { year: 2036, day: 13, month: 4 },
    { year: 2037, day: 5, month: 4 },
    { year: 2038, day: 25, month: 4 },
    { year: 2039, day: 10, month: 4 },
    { year: 2040, day: 1, month: 4 },
  ];

  /**
   * Holidays calculated relative to Easter Sunday
   */
  private readonly EASTER_RELATED_HOLIDAYS: EasterRelatedHoliday[] = [
    { days: -2, name: 'Långfredag' }, // Good Friday
    { days: 1, name: 'Annandag påsk' }, // Easter Monday
    { days: 39, name: 'Kristi himmelsfärdsdag' }, // Ascension Day
  ];

  /**
   * Cached holiday map for performance
   */
  private holidayMap: HolidayMap | null = null;

  constructor() {}

  /**
   * Check if a day is a weekend day (Saturday or Sunday)
   */
  isWeekendDay(day: moment.Moment): boolean {
    if (!day || !day.isValid()) {
      console.warn('DateHolidayService.isWeekendDay: Invalid moment provided');
      return false;
    }
    return this.WEEKEND_DAYS.includes(day.isoWeekday());
  }

  /**
   * Get the day of midsummer eve for a given year
   * Midsummer eve is always the Friday between June 19-25
   */
  private getMidsummerEveDay(year: number): number {
    if (!year || year < 1900 || year > 2100) {
      console.warn(`DateHolidayService.getMidsummerEveDay: Invalid year ${year}`);
      return 0;
    }

    for (let day = 19; day <= 25; day++) {
      const date = moment({ y: year, M: 5, d: day }); // Month 5 = June (0-indexed)
      if (date.isoWeekday() === 5) {
        // Friday
        return date.date();
      }
    }

    console.error(
      `DateHolidayService.getMidsummerEveDay: Could not find midsummer eve for year ${year}`
    );
    return 0;
  }

  /**
   * Get the day of All Saints' Day for a given year.
   * In Sweden it is always the Saturday between October 31 and November 6.
   */
  private getAllSaintsDay(year: number): { month: number; day: number } | null {
    if (!year || year < 1900 || year > 2100) {
      console.warn(`DateHolidayService.getAllSaintsDay: Invalid year ${year}`);
      return null;
    }

    const start = moment({ y: year, M: 9, d: 31 });
    for (let i = 0; i < 7; i++) {
      const date = moment(start).add(i, 'days');
      if (date.isoWeekday() === 6) {
        return {
          month: date.month() + 1,
          day: date.date(),
        };
      }
    }

    console.error(
      `DateHolidayService.getAllSaintsDay: Could not find All Saints' Day for year ${year}`
    );
    return null;
  }

  /**
   * Build the complete holiday map for all years
   * This is cached for performance
   */
  private buildHolidayMap(): HolidayMap {
    if (this.holidayMap) {
      return this.holidayMap;
    }

    const holidayMap: HolidayMap = {};

    for (const easterDate of this.EASTER_DATES) {
      const { year, month, day } = easterDate;

      // Initialize year structure
      if (!holidayMap[year]) {
        holidayMap[year] = {};
      }

      const easterSunday = moment({ y: year, M: month - 1, d: day });

      // Add Easter-related holidays
      for (const relatedHoliday of this.EASTER_RELATED_HOLIDAYS) {
        const holidayDate = moment(easterSunday).add(relatedHoliday.days, 'days');
        const holidayMonth = holidayDate.month() + 1; // Convert to 1-indexed
        const holidayDay = holidayDate.date();

        if (!holidayMap[year][holidayMonth]) {
          holidayMap[year][holidayMonth] = {};
        }
        holidayMap[year][holidayMonth][holidayDay] = relatedHoliday.name;
      }

      // Add fixed holidays
      for (const fixedHoliday of this.FIXED_HOLIDAYS) {
        const { month: fixedMonth, day: fixedDay, name } = fixedHoliday;

        if (!holidayMap[year][fixedMonth]) {
          holidayMap[year][fixedMonth] = {};
        }
        holidayMap[year][fixedMonth][fixedDay] = name;
      }

      // Add Midsummer Eve (always in June, month 6)
      const midsummerDay = this.getMidsummerEveDay(year);
      if (midsummerDay > 0) {
        if (!holidayMap[year][6]) {
          holidayMap[year][6] = {};
        }
        holidayMap[year][6][midsummerDay] = 'Midsommarafton';
      }

      // Add All Saints' Day (Saturday between Oct 31 and Nov 6)
      const allSaintsDay = this.getAllSaintsDay(year);
      if (allSaintsDay) {
        if (!holidayMap[year][allSaintsDay.month]) {
          holidayMap[year][allSaintsDay.month] = {};
        }
        holidayMap[year][allSaintsDay.month][allSaintsDay.day] = 'Alla helgons dag';
      }
    }

    this.holidayMap = holidayMap;
    return holidayMap;
  }

  /**
   * Check if a specific date is a red day (public holiday)
   * Returns the name of the holiday if it is a red day, undefined otherwise
   */
  private getHolidayName(year: number, month: number, day: number): string | undefined {
    const holidayMap = this.buildHolidayMap();

    try {
      return holidayMap[year]?.[month]?.[day];
    } catch (error) {
      console.warn(
        `DateHolidayService.getHolidayName: Error checking date ${year}-${month}-${day}`,
        error
      );
      return undefined;
    }
  }

  /**
   * Check if a moment date is a red day
   * Returns the name of the holiday if it is a red day, undefined otherwise
   */
  isRedDayDate(date: moment.Moment): string | undefined {
    if (!date || !date.isValid()) {
      console.warn('DateHolidayService.isRedDayDate: Invalid moment provided');
      return undefined;
    }

    try {
      return this.getHolidayName(date.year(), date.month() + 1, date.date());
    } catch (error) {
      console.error('DateHolidayService.isRedDayDate: Error checking red day', error);
      return undefined;
    }
  }

  /**
   * Check if a moment date is a red day (alternative implementation using format)
   * Returns the name of the holiday if it is a red day, undefined otherwise
   */
  isRedDayMoment(date: moment.Moment): string | undefined {
    if (!date || !date.isValid()) {
      console.warn('DateHolidayService.isRedDayMoment: Invalid moment provided');
      return undefined;
    }

    try {
      const year = parseInt(date.format('YYYY'), 10);
      const month = parseInt(date.format('M'), 10);
      const day = parseInt(date.format('D'), 10);

      return this.getHolidayName(year, month, day);
    } catch (error) {
      console.error('DateHolidayService.isRedDayMoment: Error checking red day', error);
      return undefined;
    }
  }

  /**
   * Get a list of week days between two dates
   */
  getWeekListDates(dateFrom: moment.Moment, dateTo: moment.Moment): WeekDayInfo[] {
    if (!dateFrom || !dateFrom.isValid() || !dateTo || !dateTo.isValid()) {
      console.error('DateHolidayService.getWeekListDates: Invalid dates provided');
      return [];
    }

    if (dateTo.isBefore(dateFrom)) {
      console.warn(
        'DateHolidayService.getWeekListDates: dateTo is before dateFrom, swapping dates'
      );
      [dateFrom, dateTo] = [dateTo, dateFrom];
    }

    const numDays = dateTo.diff(dateFrom, 'days') + 1;
    return this.getWeekList(dateFrom, numDays);
  }

  /**
   * Get a list of days for a given period starting from a date
   */
  getWeekList(startDate: moment.Moment, periodLength: number): WeekDayInfo[] {
    if (!startDate || !startDate.isValid()) {
      console.error('DateHolidayService.getWeekList: Invalid start date provided');
      return [];
    }

    if (periodLength <= 0) {
      console.warn(
        `DateHolidayService.getWeekList: Invalid period length ${periodLength}, using default 7`
      );
      periodLength = 7;
    }

    const weekList: WeekDayInfo[] = [];
    const currentDate = moment(startDate).subtract(1, 'days');

    for (let i = 0; i < periodLength; i++) {
      currentDate.add(1, 'days');

      weekList[i] = {
        header: moment(currentDate), // Clone the date
        redDay: this.isRedDayDate(currentDate),
        weekIndex: currentDate.isoWeekday(),
        weekNumber: currentDate.isoWeek(),
      };
    }

    return weekList;
  }

  /**
   * Calculate total work hours between two dates, inclusive.
   * A work day is a weekday that is not a configured red day.
   */
  getWorkHoursBetween(dateFrom: moment.Moment, dateTo: moment.Moment): number {
    return this.getWeekListDates(dateFrom, dateTo).reduce((sum, day) => {
      if (day.redDay || day.weekIndex >= 6) {
        return sum;
      }
      return sum + 8;
    }, 0);
  }

  /**
   * Calculate work hours between two dates, grouped by month number (1-12).
   * Each work day contributes 8 hours.
   */
  getWorkHoursByMonthBetween(
    dateFrom: moment.Moment,
    dateTo: moment.Moment
  ): Record<number, number> {
    return this.getWeekListDates(dateFrom, dateTo).reduce<Record<number, number>>(
      (hoursByMonth, day) => {
        if (day.redDay || day.weekIndex >= 6) {
          return hoursByMonth;
        }

        const month = day.header.month() + 1;
        if (!hoursByMonth[month]) {
          hoursByMonth[month] = 0;
        }
        hoursByMonth[month] += 8;
        return hoursByMonth;
      },
      {}
    );
  }

  /**
   * Split an inclusive date interval into work hours per month.
   */
  getMonthWorkHoursBetween(
    dateFrom: moment.Moment,
    dateTo: moment.Moment
  ): MonthWorkHours[] {
    const workHoursByMonth = this.getWorkHoursByMonthBetween(dateFrom, dateTo);
    const months = new Map<string, MonthWorkHours>();

    this.getWeekListDates(dateFrom, dateTo).forEach((day) => {
      const year = day.header.year();
      const month = day.header.month() + 1;
      const key = `${year}-${month}`;

      if (months.has(key)) {
        return;
      }

      months.set(key, {
        year,
        month,
        monthName: day.header.locale('sv').format('MMMM'),
        workHours: workHoursByMonth[month] || 0,
      });
    });

    return Array.from(months.values());
  }

  /**
   * Get work hours per month for a business year running from July to June.
   * Example: startYear 2024 => 2024-07-01 .. 2025-06-30
   */
  getBusinessYearMonthWorkHours(startYear: number): MonthWorkHours[] {
    return this.getMonthWorkHoursBetween(
      moment({ y: startYear, M: 6, d: 1 }),
      moment({ y: startYear + 1, M: 5, d: 30 })
    );
  }

  getBusinessYearMonthWorkHoursMap(startYear: number): BusinessYearMonthWorkHoursMap {
    const monthKeyByNumber: Record<number, keyof BusinessYearMonthWorkHoursMap> = {
      7: 'jul',
      8: 'aug',
      9: 'sep',
      10: 'okt',
      11: 'nov',
      12: 'dec',
      1: 'jan',
      2: 'feb',
      3: 'mar',
      4: 'apr',
      5: 'maj',
      6: 'jun',
    };

    const result: BusinessYearMonthWorkHoursMap = {
      jul: 0,
      aug: 0,
      sep: 0,
      okt: 0,
      nov: 0,
      dec: 0,
      jan: 0,
      feb: 0,
      mar: 0,
      apr: 0,
      maj: 0,
      jun: 0,
    };

    this.getBusinessYearMonthWorkHours(startYear).forEach((entry) => {
      result[monthKeyByNumber[entry.month]] = entry.workHours;
    });

    return result;
  }

  calculateBillableBudgetByBusinessYear(
    availableHours: BusinessYearMonthValueMap,
    distributions: BillableBudgetDistributions,
    ownAnnualHours: BillableBudgetAnnualHours,
    contractorAnnualHours: BillableBudgetAnnualHours
  ): BillableBudgetCalculation {
    return {
      own: this.calculateBillableBudgetBreakdown(availableHours, distributions, ownAnnualHours),
      contractor: this.calculateBillableBudgetBreakdown(
        availableHours,
        distributions,
        contractorAnnualHours
      ),
    };
  }

  calculateBillableBudgetByBusinessYearInputs(
    startYear: number,
    ownInputs: BillableBudgetInputs,
    contractorInputs: BillableBudgetInputs
  ): BillableBudgetCalculation {
    return this.calculateBillableBudgetByBusinessYear(
      this.getBusinessYearMonthWorkHoursMap(startYear),
      this.DEFAULT_BILLABLE_BUDGET_DISTRIBUTIONS,
      this.toAnnualBudgetHours(ownInputs),
      this.toAnnualBudgetHours(contractorInputs)
    );
  }

  private calculateBillableBudgetBreakdown(
    availableHours: BusinessYearMonthValueMap,
    distributions: BillableBudgetDistributions,
    annualHours: BillableBudgetAnnualHours
  ): BillableBudgetBreakdown {
    const vacation = this.distributeAnnualHours(annualHours.vacation, distributions.vacation);
    const sick = this.distributeAnnualHours(annualHours.sick, distributions.sick);
    const internal = this.distributeAnnualHours(annualHours.internal, distributions.internal);
    const billable = this.createEmptyBusinessYearMonthMap();

    this.BUSINESS_YEAR_MONTH_KEYS.forEach((key) => {
      billable[key] = availableHours[key] - vacation[key] - sick[key] - internal[key];
    });

    return {
      vacation,
      sick,
      internal,
      billable,
      yearlyTotal:
        this.sumBusinessYearMap(availableHours) -
        annualHours.vacation -
        annualHours.sick -
        annualHours.internal,
    };
  }

  private distributeAnnualHours(
    annualHours: number,
    percentages: BusinessYearMonthValueMap
  ): BusinessYearMonthValueMap {
    const distributed = this.createEmptyBusinessYearMonthMap();
    this.BUSINESS_YEAR_MONTH_KEYS.forEach((key) => {
      distributed[key] = annualHours * (percentages[key] / 100);
    });

    return distributed;
  }

  private sumBusinessYearMap(values: BusinessYearMonthValueMap): number {
    return this.BUSINESS_YEAR_MONTH_KEYS.reduce((sum, key) => sum + values[key], 0);
  }

  private createEmptyBusinessYearMonthMap(): BusinessYearMonthValueMap {
    return {
      jul: 0,
      aug: 0,
      sep: 0,
      okt: 0,
      nov: 0,
      dec: 0,
      jan: 0,
      feb: 0,
      mar: 0,
      apr: 0,
      maj: 0,
      jun: 0,
    };
  }

  private toAnnualBudgetHours(inputs: BillableBudgetInputs): BillableBudgetAnnualHours {
    return {
      vacation: inputs.semester,
      sick: inputs.genomsnittSjuk,
      internal: inputs.kitscon + inputs.kompetensutveckling + inputs.månadsmöten,
    };
  }
}
